// src/context/CustomerAuthContext/CustomerAuthContext.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { ApiResponse } from "../../types/Api";
import {
  giris,
  kayitOl,
  GirisRequest,
  KayitOlRequest,
  MusteriAuthResponse,
} from "../../api/auth/customerAuth";

/* --------------------------- helpers --------------------------- */
// JWT içinden payload decode (UTF-8 güvenli)
function decodeJwt(token: string): any | null {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

// API normalize (camel/Pascal karışıklığını toparlar)
function normalize<T = unknown>(raw: any): ApiResponse<T> {
  return {
    success: raw?.success ?? raw?.Success ?? false,
    errorCode: raw?.errorCode ?? raw?.ErrorCode,
    errorDetail:
      raw?.errorDetail ?? raw?.ErrorDetail ?? raw?.message ?? raw?.Message,
    data: (raw?.data ?? raw?.Data) as T,
  };
}

/* --------------------------- types --------------------------- */
type Customer = {
  id: number;
  name?: string;
  email?: string;
  telefon?: string;
  ad?: string;
  soyad?: string;
};

type CustomerAuthContextType = {
  customer: Customer | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (
    payload: GirisRequest
  ) => Promise<ApiResponse<MusteriAuthResponse>>;
  register: (
    payload: KayitOlRequest
  ) => Promise<ApiResponse<MusteriAuthResponse>>;
  logout: () => void;

  /** Bazı yerlerde çağırmak isteyebilirsin diye exposed ettim */
  setTokenAndUserFromToken: (token: string) => void;
  /** Profil güncellemesinden sonra context + localStorage'ı tazele */
  updateCustomerProfile: (data: { ad?: string; soyad?: string; email?: string; telefon?: string }) => void;
};

/* --------------------------- context --------------------------- */
const CustomerAuthContext = createContext<CustomerAuthContextType | undefined>(
  undefined
);

export const useCustomerAuth = () => {
  const v = useContext(CustomerAuthContext);
  if (!v)
    throw new Error(
      "useCustomerAuth must be used within CustomerAuthProvider"
    );
  return v;
};

/* --------------------------- provider --------------------------- */
export const CustomerAuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // localStorage’ı lazy initializer ile senkron okuyup flicker’ı azaltıyoruz
  const [token, setToken] = useState<string | null>(() =>
    localStorage.getItem("cust_token")
  );
  const [customer, setCustomer] = useState<Customer | null>(() => {
    const id = localStorage.getItem("cust_id");
    if (!id) return null;
    return {
      id: Number(id),
      name: localStorage.getItem("cust_name") || undefined,
      email: localStorage.getItem("cust_email") || undefined,
      telefon: localStorage.getItem("cust_telefon") || undefined,
      ad: localStorage.getItem("cust_ad") || undefined,
      soyad: localStorage.getItem("cust_soyad") || undefined,
    };
  });

  const [loading, setLoading] = useState<boolean>(true);

  // İlk mount’ta state’i kesinleştir
  useEffect(() => {
    setLoading(true);
    try {
      const t = localStorage.getItem("cust_token");
      const id = localStorage.getItem("cust_id");
      const name = localStorage.getItem("cust_name") || undefined;
      const email = localStorage.getItem("cust_email") || undefined;

      if (t && id) {
        const telefon = localStorage.getItem("cust_telefon") || undefined;
        const ad = localStorage.getItem("cust_ad") || undefined;
        const soyad = localStorage.getItem("cust_soyad") || undefined;
        setToken(t);
        setCustomer({ id: Number(id), name, email, telefon, ad, soyad });
      } else {
        setToken(null);
        setCustomer(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Çoklu sekme senkronu
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (
        e.key === "cust_token" ||
        e.key === "cust_id" ||
        e.key === "cust_name" ||
        e.key === "cust_email" ||
        e.key === "cust_telefon" ||
        e.key === "cust_ad" ||
        e.key === "cust_soyad"
      ) {
        const t = localStorage.getItem("cust_token");
        const id = localStorage.getItem("cust_id");
        const name = localStorage.getItem("cust_name") || undefined;
        const email = localStorage.getItem("cust_email") || undefined;
        const telefon = localStorage.getItem("cust_telefon") || undefined;
        const ad = localStorage.getItem("cust_ad") || undefined;
        const soyad = localStorage.getItem("cust_soyad") || undefined;

        setToken(t);
        setCustomer(t && id ? { id: Number(id), name, email, telefon, ad, soyad } : null);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Tek noktadan persist
  const persistFromAuthResponse = (res: MusteriAuthResponse) => {
    const id = Number(res.musteriId);
    const tk = String(res.token);
    if (!id || !tk) return;

    localStorage.setItem("cust_id", String(id));
    localStorage.setItem("cust_token", tk);

    const payload = decodeJwt(tk);
    const adVal = payload?.ad;
    const soyadVal = payload?.soyad;
    const email = payload?.email ?? payload?.Email ?? undefined;
    const fullName = [adVal, soyadVal].filter(Boolean).join(" ").trim() || undefined;

    if (fullName) localStorage.setItem("cust_name", fullName);
    if (email) localStorage.setItem("cust_email", email);
    if (adVal) localStorage.setItem("cust_ad", adVal);
    if (soyadVal) localStorage.setItem("cust_soyad", soyadVal);

    setToken(tk);
    setCustomer({ id, name: fullName, email, ad: adVal, soyad: soyadVal });
  };

  // Dışarıdan sadece token geldiyse (ör: SSO, magic link) kullanıcıyı hydrate et
  const setTokenAndUserFromToken = (tk: string) => {
    if (!tk) return;
    const payload = decodeJwt(tk) || {};
    const subId =
      Number(payload?.nameid ?? payload?.sub ?? payload?.NameId ?? 0) || 0;
    const ad = payload?.ad;
    const soyad = payload?.soyad;
    const email = payload?.email ?? payload?.Email ?? undefined;
    const fullName = [ad, soyad].filter(Boolean).join(" ").trim() || undefined;

    localStorage.setItem("cust_token", tk);
    if (subId) localStorage.setItem("cust_id", String(subId));
    else localStorage.removeItem("cust_id");

    if (fullName) localStorage.setItem("cust_name", fullName);
    else localStorage.removeItem("cust_name");

    if (email) localStorage.setItem("cust_email", email);
    else localStorage.removeItem("cust_email");

    if (ad) localStorage.setItem("cust_ad", ad);
    else localStorage.removeItem("cust_ad");

    if (soyad) localStorage.setItem("cust_soyad", soyad);
    else localStorage.removeItem("cust_soyad");

    setToken(tk);
    setCustomer(subId ? { id: subId, name: fullName, email, ad, soyad } : null);
  };

  // Actions
  const login = async (payload: GirisRequest) => {
    setLoading(true);
    try {
      const raw = await giris(payload);
      const res = normalize<MusteriAuthResponse>(raw);
      if (res.success && res.data) persistFromAuthResponse(res.data);
      return res;
    } finally {
      setLoading(false);
    }
  };

  const register = async (payload: KayitOlRequest) => {
    setLoading(true);
    try {
      const raw = await kayitOl(payload);
      const res = normalize<MusteriAuthResponse>(raw);
      if (res.success && res.data) persistFromAuthResponse(res.data);
      return res;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("cust_token");
    localStorage.removeItem("cust_id");
    localStorage.removeItem("cust_name");
    localStorage.removeItem("cust_email");
    localStorage.removeItem("cust_telefon");
    localStorage.removeItem("cust_ad");
    localStorage.removeItem("cust_soyad");
    setToken(null);
    setCustomer(null);
  };

  const updateCustomerProfile = (data: { ad?: string; soyad?: string; email?: string; telefon?: string }) => {
    const fullName = [data.ad, data.soyad].filter(Boolean).join(" ").trim();
    if (fullName) localStorage.setItem("cust_name", fullName);
    if (data.ad !== undefined) localStorage.setItem("cust_ad", data.ad);
    if (data.soyad !== undefined) localStorage.setItem("cust_soyad", data.soyad);
    if (data.email !== undefined) localStorage.setItem("cust_email", data.email);
    if (data.telefon !== undefined) localStorage.setItem("cust_telefon", data.telefon);
    setCustomer((prev) =>
      prev
        ? {
            ...prev,
            name: fullName || prev.name,
            email: data.email ?? prev.email,
            telefon: data.telefon ?? prev.telefon,
            ad: data.ad ?? prev.ad,
            soyad: data.soyad ?? prev.soyad,
          }
        : prev
    );
  };

  const isAuthenticated = !!token;

  const value = useMemo(
    () => ({
      customer,
      token,
      isAuthenticated,
      loading,
      login,
      register,
      logout,
      setTokenAndUserFromToken,
      updateCustomerProfile,
    }),
    [customer, token, loading]
  );

  return (
    <CustomerAuthContext.Provider value={value}>
      {children}
    </CustomerAuthContext.Provider>
  );
};
